import React, { useState } from "react";
import { Link } from "react-router-dom";
import AuthLayout from "../components/AuthLayout";
import { Button } from "../components/ui/Button";
import { Input, Label } from "../components/ui/Input";
import { CheckCircle, Warning, EnvelopeSimple } from "@phosphor-icons/react";
import { api, extractError } from "../lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setDone(true);
    } catch (err) {
      setError(extractError(err, "Anfrage konnte nicht verarbeitet werden."));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthLayout
        title="E-Mail wurde versendet"
        subtitle="Falls ein Konto mit dieser Adresse existiert."
        footer={
          <p className="text-xs text-warm-600 text-center">
            <Link
              to="/login"
              data-testid="forgot-back-login"
              className="font-semibold text-ink-900 hover:text-brand-500 transition-crisp"
            >
              Zurück zur Anmeldung
            </Link>
          </p>
        }
      >
        <div
          data-testid="forgot-success"
          className="flex items-start gap-3 border border-status-aktiv-border bg-status-aktiv-bg p-4 rounded-sm"
        >
          <CheckCircle size={18} weight="fill" className="text-status-aktiv-text mt-0.5 shrink-0" />
          <div className="text-sm text-ink-900 leading-relaxed">
            Wenn ein aktives Konto mit der Adresse <strong>{email}</strong> existiert,
            haben wir Ihnen einen Link zum Zurücksetzen des Passworts gesendet. Der Link ist
            <strong> 60 Minuten</strong> gültig.
            <p className="mt-2 text-xs text-warm-600">
              Bitte prüfen Sie auch Ihren Spam-Ordner. Sollte keine E-Mail eintreffen,
              fragen Sie es einfach erneut an.
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Passwort vergessen"
      subtitle="Wir senden Ihnen einen Link zum Zurücksetzen."
      footer={
        <p className="text-xs text-warm-600 text-center">
          <Link
            to="/login"
            data-testid="forgot-back-login"
            className="font-semibold text-ink-900 hover:text-brand-500 transition-crisp"
          >
            Zurück zur Anmeldung
          </Link>
        </p>
      }
    >
      <form
        className="space-y-5"
        onSubmit={handleSubmit}
        noValidate
        data-testid="forgot-form"
      >
        <div>
          <Label htmlFor="email">E-Mail-Adresse</Label>
          <div className="relative">
            <EnvelopeSimple
              size={16}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-400 pointer-events-none"
            />
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ihre.email@firma.at"
              className="pl-9"
              data-testid="forgot-email-input"
            />
          </div>
        </div>

        {error && (
          <div
            data-testid="forgot-error"
            role="alert"
            className="flex items-start gap-2 border border-status-abgelehnt-border bg-status-abgelehnt-bg px-3 py-2 rounded-sm text-sm text-status-abgelehnt-text"
          >
            <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button
          type="submit"
          variant="brand"
          size="lg"
          className="w-full"
          disabled={submitting || !email.trim()}
          data-testid="forgot-submit-btn"
        >
          {submitting ? "Wird gesendet…" : "Reset-Link anfordern"}
        </Button>
      </form>
    </AuthLayout>
  );
}
